import { apiFetch, apiFetchBlob, apiUpload, v1Path } from './api';

export type AttachmentDto = {
  id: string;
  filename: string;
  requirement_label: string | null;
  content_type: string;
  byte_size: number;
  download_url: string;
  uploaded_by_id: string;
  created_at: string;
};

const IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

/** MIME to use when rendering a stored image, or null when it is not an image. */
export function imageMimeForAttachment(row: { content_type: string; filename: string }): string | null {
  const mime = row.content_type.trim().toLowerCase();
  if (mime === 'image/jpg') return 'image/jpeg';
  if (IMAGE_MIME_TYPES.has(mime)) return mime;
  const ext = row.filename.split('.').pop()?.toLowerCase();
  if (!ext || !IMAGE_EXTENSIONS.has(ext)) return null;
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return `image/${ext}`;
}

export function isImageAttachment(row: { content_type: string; filename: string }): boolean {
  return imageMimeForAttachment(row) !== null;
}

const previewCache = new Map<string, Promise<string>>();

/** Authenticated object URL for an image. Cached so Strict Mode remounts do not drop the preview. */
export function previewUrlForAttachment(downloadUrl: string): Promise<string> {
  if (typeof downloadUrl !== 'string' || downloadUrl.length === 0) {
    return Promise.reject(new Error('previewUrlForAttachment requires a download URL.'));
  }
  const hit = previewCache.get(downloadUrl);
  if (hit) return hit;
  const pending = apiFetchBlob(downloadUrl)
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      if (typeof url !== 'string' || url.length === 0) {
        throw new Error('Could not create an image preview.');
      }
      return url;
    })
    .catch((err: unknown) => {
      previewCache.delete(downloadUrl);
      throw err;
    });
  previewCache.set(downloadUrl, pending);
  return pending;
}

export function clearAttachmentPreviewCache(): void {
  previewCache.clear();
}

function isAttachmentDto(value: unknown): value is AttachmentDto {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    typeof row.filename === 'string' &&
    (row.requirement_label === null || row.requirement_label === undefined || typeof row.requirement_label === 'string') &&
    typeof row.content_type === 'string' &&
    typeof row.byte_size === 'number' &&
    typeof row.download_url === 'string'
  );
}

export async function uploadPendingAttachment(file: File): Promise<AttachmentDto> {
  const dto = await apiUpload<unknown>(v1Path('/attachments'), file, { subject_type: 'pending' });
  if (!isAttachmentDto(dto)) {
    throw new Error('Malformed attachment response.');
  }
  return dto;
}

export async function uploadWorkItemAttachment(projectId: string, workItemId: string, file: File): Promise<AttachmentDto> {
  return uploadWorkItemDocument(projectId, workItemId, file, null);
}

export async function uploadWorkItemDocument(
  projectId: string,
  workItemId: string,
  file: File,
  requirementLabel: string | null,
): Promise<AttachmentDto> {
  const dto = await apiUpload<unknown>(
    v1Path(`/projects/${projectId}/work-items/${workItemId}/attachments`),
    file,
    requirementLabel ? { requirement_label: requirementLabel } : undefined,
  );
  if (!isAttachmentDto(dto)) {
    throw new Error('Malformed attachment response.');
  }
  return dto;
}

export async function listWorkItemAttachments(projectId: string, workItemId: string): Promise<AttachmentDto[]> {
  const res = await apiFetch<unknown>(v1Path(`/projects/${projectId}/work-items/${workItemId}/attachments`));
  if (!res || typeof res !== 'object' || !Array.isArray((res as { items?: unknown }).items)) {
    throw new Error('Malformed attachment list.');
  }
  const items = (res as { items: unknown[] }).items;
  if (!items.every(isAttachmentDto)) {
    throw new Error('Malformed attachment list entry.');
  }
  return items;
}

export async function deleteAttachment(attachmentId: string): Promise<void> {
  await apiFetch(v1Path(`/attachments/${attachmentId}`), { method: 'DELETE' });
}
